import { constants, database, dbViews } from 'lib_ui-services';
import downloadTheFile from '../../namespace/relation/exportHelpers/exportHelper';

const { EXPORT_TYPES } = constants;

const _p = {
    downloadTheFile,
    showImportMessage,
    showFailedImportMessage,
    handleCsvDownload,
    database,
    dbViews,
    resolveImportedRelation
};

export const _private = _p;

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 */

export default {
    verb: 'didCreate',
    namespace: 'import',
    relation: 'import',
    description: 'Notify the user that the import has completed',
    // this is the actual logic:
    logic: didCreate,
    // error handling to avoid the progress staying on the screen:
    onError
};

/**
 * @param {{
 *      error: Error;
 *      data: T;
 *      context: Context;
 *      dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>,
 * }} parameters
 * */
function onError({ error, dispatch }) {
    dispatch(
        {
            mainTitle: 'Importing'
        },
        { verb: 'reset', namespace: 'application', relation: 'progress' }
    );
    throw error;
}

/**
 * Creates an array of error records from the provided failed records.
 * @param {Array} failedRecords - An array of failed records.
 * @returns {Array} An array of error records with consolidated error messages.
 */
const createErrorRecords = failedRecords => {
    return failedRecords.map(failedRecord => ({
        ...failedRecord,
        errors: failedRecord.errors
            // It's possible that the error is a string
            .map(e => e.message || e)
            // Some messages end in ". ", which would leave "digits. , next error" once joined.
            .map(message => (typeof message === 'string' ? message.trim() : message))
            .filter(Boolean)
            .join(', ')
    }));
};

/**
 * Notify the user that the import has completed.
 *
 * Shared by every import type, so it speaks of rows (lines of the file) and records (what was
 * written) and keeps the two apart (#2282): an order import groups many rows into one record,
 * and a Serialized Each import fans one row out into many. It gives counts only — the reason
 * each row failed is in the failed-records CSV.
 */
async function showImportMessage(data, dispatch) {
    const { result: { title, failed = [] } = {} } = data;

    const summary = summarizeImport(data.result);
    let variant = 'success';
    let message = describeImported(summary);

    if (failed.length) {
        const failureClause = describeFailures(summary);
        message += ` ${failureClause}`;
        variant = 'warning';

        // "Import Failed" only when nothing was imported; a partly successful import did not fail.
        let dialogTitle = 'Import Completed with Errors';
        if (summary.recordsImported === 0) {
            message = `Import "${title}" failed: ${failureClause}`;
            variant = 'error';
            dialogTitle = 'Import Failed';
        }

        // Create the failed records CSV
        const failedRecords = createErrorRecords(failed);

        // Show the option to download import errors to a CSV file
        await _p.handleCsvDownload(data, dispatch, title, failedRecords, message, dialogTitle);
    }

    dispatch(
        {
            message,
            timeout: 5000,
            variant,
            type: constants.notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );

    // Close the import form after successful message (only if all records succeeded)
    if (failed.length === 0) {
        dispatch({}, { verb: 'close', namespace: 'import', relation: 'import' });
    }
}

/**
 * Rows and records imported and failed.
 *
 * Taken from the server when it counted them apart. Otherwise the record count prefers the
 * server's — it accounts for server-side fan-out (e.g. Serialized Each model+quantity, where 3
 * source rows can create 10 items). Rows are then known only when nothing failed: `failed` holds
 * records, and after fan-out a failed item cannot be traced to its row (nor is a row whose items
 * partly failed either imported or failed). So rows are left unknown rather than miscounted.
 */
function summarizeImport({ totalRecords = 0, totalRows, importedRecords, recordsCreated, counts, failed = [] } = {}) {
    if (counts) return { ...counts, rowsKnown: true };

    const numFailed = failed.length;
    const recordsImported =
        importedRecords != null ? importedRecords : Math.round((recordsCreated ?? totalRecords) - numFailed);
    return {
        rowsKnown: numFailed === 0,
        rowsImported: totalRows ?? totalRecords,
        recordsImported,
        recordsFailed: numFailed
    };
}

function describeImported({ rowsKnown, rowsImported, recordsImported }) {
    const recordsClause = countOf(recordsImported, 'record');
    return rowsKnown ? `${countOf(rowsImported, 'row')} imported into ${recordsClause}.` : `${recordsClause} imported.`;
}

function describeFailures({ rowsKnown, rowsFailed, recordsFailed }) {
    if (!rowsKnown) return `${countOf(recordsFailed, 'record')} failed.`;
    const rowsClause = `${countOf(rowsFailed, 'row')} failed`;
    if (!recordsFailed) return `${rowsClause}.`;
    return `${rowsClause}; ${countOf(recordsFailed, 'record')} ${recordsFailed === 1 ? 'was' : 'were'} not imported.`;
}

function countOf(count, noun) {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Offer the failed-records CSV for rows that never reached the server (#2282).
 *
 * The same dialog and file a finished import gives, for an import stopped before it was sent —
 * so the operator still learns why each row failed rather than only that the import was aborted.
 * Not awaited by callers: the dialog's promise settles only on Cancel.
 *
 * @param {Object} options
 * @param {Object[]} options.columns the import's columns
 * @param {Function} options.dispatch
 * @param {string} options.title import name
 * @param {Object[]} options.failedRecords rows carrying `errors`, typed values restored
 * @param {string} options.message
 */
export function offerFailedRecordsDownload({ columns, dispatch, title, failedRecords, message }) {
    return _p.handleCsvDownload(
        { newRecord: { columns } },
        dispatch,
        title,
        createErrorRecords(failedRecords),
        message
    );
}

/**
 * Handle the Import error CSV download process
 */
async function handleCsvDownload(data, dispatch, title, failed, message, dialogTitle) {
    // Add the "Errors" column as the first column,
    // along with any of the required column metadata used for sorting and displaying the columns in the exported file
    const columns = [
        { title: 'Errors', propertyName: 'errors', sequence: 0, id: 'errors' },
        ...data.newRecord.columns.map((column, index) => ({
            ...column._meta,
            sequence: column._meta.sequence !== undefined ? column._meta.sequence + 1 : index + 1
        }))
    ];

    const downloadErrorCsv = () => _p.downloadTheFile(title, failed, columns, EXPORT_TYPES.csv);
    await _p.showFailedImportMessage(dispatch, downloadErrorCsv, message, dialogTitle);
}

/**
 * Show the Import error message and prompt the user to download the error CSV file
 */
async function showFailedImportMessage(dispatch, fn, message, dialogTitle = 'Import Failed') {
    return new Promise(resolve => {
        dispatch(
            {
                title: dialogTitle,
                message: [
                    message,
                    'Would you like to download the failed records?',
                    'Contact 1-877-373-0390 for support.'
                ],
                okButtonText: 'Download',
                cancelButtonText: 'Cancel',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Warning,
                okAction: () => {
                    fn();
                },
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function didCreate({ data, dispatch, log }) {
    dispatch(
        {
            mainTitle: 'Importing'
        },
        { verb: 'reset', namespace: 'application', relation: 'progress' }
    );

    // Handle import notifications and possible import error CSV download
    await _p.showImportMessage(data, dispatch);

    // Socket notifications during batches do not reliably update the client list,
    // so trigger a targeted re-sync of the imported relation after all batches complete.
    const { namespace: namespaceTitle, relation: relationTitle } = _p.resolveImportedRelation(data.newRecord);
    if (namespaceTitle && relationTitle) {
        try {
            const syncPromise = _p.database.get()?.synchronization?.syncOneRelation({
                namespaceTitle,
                relationTitle
            });
            if (syncPromise) {
                // Sync is best-effort — a failure must not surface as an error after the success UI has already shown
                await syncPromise.catch(err => log.warn('Post-import sync failed', err));
            }
        } catch (err) {
            log.warn('Post-import sync skipped', err);
        }
        // Even with the records synced into Loki, a limitSyncSize relation applies the #332
        // display gate that pins the grid to the last server page — so freshly-imported records
        // (delivered via socket, upserted locally) stay hidden until the user changes a filter.
        // Reset the gate for that relation's views so they appear immediately (mirrors what a
        // user-driven filter change does). No-op for non-limitSyncSize relations.
        try {
            await _p.dbViews.refreshViewsForRelation(namespaceTitle, relationTitle);
        } catch (err) {
            log.warn('Post-import view refresh failed', err);
        }
    }
}

/**
 * Resolve the relation whose grid should be refreshed after an import completes.
 *
 * Imports POST to `newRecord.foreignNamespace:foreignRelation`. For most imports that is where
 * the records land. Serialized Each "Auto Serializing" (model+quantity) and "RFID Tag" imports,
 * however, POST to a VIRTUAL `transaction:itemsByModel` / `transaction:itemByTagId` relation that
 * the rules-engine bridges into real `item:item` records — so the grid to refresh is `item:item`,
 * not the empty virtual relation.
 * @param {{foreignNamespace?: string, foreignRelation?: string}} [newRecord]
 * @returns {{namespace: string|undefined, relation: string|undefined}}
 */
function resolveImportedRelation(newRecord) {
    const namespace = newRecord?.foreignNamespace;
    const relation = newRecord?.foreignRelation;
    if (namespace === 'transaction' && (relation === 'itemsByModel' || relation === 'itemByTagId')) {
        return { namespace: 'item', relation: 'item' };
    }
    return { namespace, relation };
}
